<template>
  <div class="import-dialog">
    <div class="import-toolbar">
      <div class="import-toolbar__left">
        <el-upload
          class="import-upload"
          :show-file-list="false"
          :auto-upload="false"
          accept=".xlsx,.xls"
          @change="handleFileChange"
        >
          <button type="button" class="import-action import-action--primary">
            <i class="fas fa-file-upload" aria-hidden="true"></i>
            {{ $t("importUploadExcel") }}
          </button>
        </el-upload>
        <button
          type="button"
          class="import-action import-action--outline"
          @click="downloadExample('Nümunə.xlsx')"
        >
          <i class="fas fa-download" aria-hidden="true"></i>
          {{ $t("importDownloadSample") }}
        </button>
      </div>

      <div class="import-toolbar__meta">
        <span class="import-chip">
          <i class="fas fa-list" aria-hidden="true"></i>
          {{ tableData.length }} {{ $t("importRows") }}
        </span>
        <span v-if="invalidCount" class="import-chip import-chip--warn">
          <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          {{ invalidCount }} {{ $t("importEmptyFields") }}
        </span>
        <span v-if="fileName" class="import-chip import-chip--file" :title="fileName">
          <i class="fas fa-file-excel" aria-hidden="true"></i>
          {{ fileName }}
        </span>
      </div>
    </div>

    <div v-if="!tableData.length" class="import-empty">
      <div class="import-empty__icon" aria-hidden="true">
        <i class="fas fa-file-excel"></i>
      </div>
      <h4 class="import-empty__title">{{ $t("importEmptyTitle") }}</h4>
      <p class="import-empty__text">
        {{ $t("importEmptyText") }}
      </p>
    </div>

    <div v-else class="import-table-wrap">
      <el-table
        size="small"
        class="import-table"
        max-height="460"
        :data="tableData"
        stripe
        border
      >
        <el-table-column label="#" width="48" fixed="left">
          <template #default="scope">
            <span class="import-row-index">{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="vip" label="VIP" width="60" align="center">
          <template #default="scope">
            <el-checkbox v-model="scope.row.vip" size="small" />
          </template>
        </el-table-column>
        <el-table-column label="PIN" width="90">
          <template #default="scope">
            <el-input
              v-model="scope.row.pin"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.pin }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="lastName" :label="$t('commonLastName')" width="120">
          <template #default="scope">
            <el-input
              v-model="scope.row.lastName"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.lastName }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="firstName" :label="$t('commonName')" width="120">
          <template #default="scope">
            <el-input
              v-model="scope.row.firstName"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.firstName }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="middleName" :label="$t('commonFatherName')" width="120">
          <template #default="scope">
            <el-input
              v-model="scope.row.middleName"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.middleName }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="sex" :label="$t('commonSex')" width="95">
          <template #default="scope">
            <el-select
              v-model="scope.row.sex"
              class="import-input"
              :placeholder="$t('commonSelect')"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.sex }"
            >
              <el-option :label="$t('commonMale')" value="m" />
              <el-option :label="$t('commonFemale')" value="f" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="dob" :label="$t('commonBirthDate')" width="125">
          <template #default="scope">
            <el-date-picker
              v-model="scope.row.dob"
              class="import-input"
              style="width: 100%"
              format="DD.MM.YYYY"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.dob }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="phone" :label="$t('commonPhoneShort')" width="120">
          <template #default="scope">
            <el-input
              v-model="scope.row.phone"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.phone }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="address" :label="$t('commonAddress')" min-width="140">
          <template #default="scope">
            <el-input
              v-model="scope.row.address"
              class="import-input"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.address }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="groupId" :label="$t('commonProgram')" width="150">
          <template #default="scope">
            <el-select
              v-model="scope.row.groupId"
              :placeholder="$t('commonSelect')"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.groupId }"
            >
              <el-option
                v-for="item in groupOptions"
                :key="item.groupId"
                :label="item.groupName"
                :value="item.groupId"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="startDate" :label="$t('commonStart')" width="125">
          <template #default="scope">
            <el-date-picker
              v-model="scope.row.startDate"
              class="import-input"
              style="width: 100%"
              :disabled-date="disabledDate"
              format="DD.MM.YYYY"
              size="small"
              :class="{ 'is-invalid-field': !scope.row.startDate }"
            />
          </template>
        </el-table-column>
        <el-table-column prop="isEmployee" :label="$t('commonStatus')" width="115">
          <template #default="scope">
            <el-select
              v-model="scope.row.isEmployee"
              class="import-input"
              :placeholder="$t('commonSelect')"
              size="small"
              :class="{ 'is-invalid-field': scope.row.isEmployee == null }"
            >
              <el-option :label="$t('commonEmployee')" :value="true" />
              <el-option :label="$t('commonFamilyMember')" :value="false" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('mainInsured')" width="110">
          <template #default="scope">
            <el-input
              v-model="scope.row.insuredDependantId"
              class="import-input uppercase-input"
              size="small"
              placeholder="FİN"
              :disabled="scope.row.isEmployee !== false"
              :class="{ 'is-invalid-field': scope.row.isEmployee === false && (!scope.row.insuredDependantId || !scope.row.insuredDependantId.toString().trim()) }"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="import-notes">
      <label class="import-notes__label">{{ $t("commonNotes") }}</label>
      <el-input
        v-model="descr"
        type="textarea"
        :rows="2"
        :placeholder="$t('importNotesPlaceholder')"
      />
    </div>

    <div class="insured-dialog-footer">
      <button type="button" class="insured-dialog-btn insured-dialog-btn--muted" @click="close">
        {{ $t("commonCancel") }}
      </button>
      <button
        type="button"
        class="insured-dialog-btn insured-dialog-btn--primary"
        :disabled="!tableData.length"
        @click="insert"
      >
        {{ $t("commonImportAction") }}
      </button>
    </div>
  </div>
</template>

<script>
import { getPolicyId } from "@/project/Source/routes";

import Source from "../../Source/claim.js";
import saveAs from "save-as";
import * as XLSX from "xlsx";
import { ElNotification } from "element-plus";
import { notifyRequestSuccess } from "@/project/Source/utils.js";

function normalizeSex(val) {
  if (val == null) return null;
  const s = String(val).trim().toLowerCase();
  if (
    s === "m" ||
    s === "k" ||
    s === "kisi" ||
    s === "kişi" ||
    s === "male" ||
    s === "мужской" ||
    s === "муж" ||
    s === "м" ||
    s === "1"
  ) {
    return "m";
  }
  if (
    s === "f" ||
    s === "q" ||
    s === "qadin" ||
    s === "qadın" ||
    s === "female" ||
    s === "женский" ||
    s === "жен" ||
    s === "ж" ||
    s === "2"
  ) {
    return "f";
  }
  return null;
}

function normalizeEmployeeStatus(val) {
  if (val == null) return null;
  if (typeof val === "boolean") return val;
  const s = String(val).trim().toLowerCase();
  if (
    s === "işçi" ||
    s === "isci" ||
    s === "employee" ||
    s === "сотрудник" ||
    s === "worker" ||
    s === "1" ||
    s === "true"
  ) {
    return true;
  }
  if (
    s === "ailə üzvü" ||
    s === "aile uzvu" ||
    s === "ailə" ||
    s === "aile" ||
    s === "family" ||
    s === "член семьи" ||
    s === "0" ||
    s === "false"
  ) {
    return false;
  }
  return null;
}

function parseExcelDate(val) {
  if (val == null || val === "") return null;
  if (val instanceof Date && !isNaN(val)) return val;
  if (typeof val === "number") {
    const excelStartDate = new Date(1900, 0, 1);
    return new Date(excelStartDate.getTime() + (val - 1) * 86400000);
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    const dotMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (dotMatch) {
      return new Date(Number(dotMatch[3]), Number(dotMatch[2]) - 1, Number(dotMatch[1]));
    }
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      return new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]));
    }
    const d = new Date(trimmed);
    if (!isNaN(d)) return d;
  }
  return null;
}

function matchGroupId(val, groupOptions) {
  if (val == null || val === "" || !groupOptions || !groupOptions.length) return null;
  const trimmed = String(val).trim().toLowerCase();
  const match = groupOptions.find(
    (g) =>
      String(g.groupName || "").trim().toLowerCase() === trimmed ||
      String(g.groupId) === trimmed
  );
  return match ? match.groupId : null;
}

export default {
  name: "InsuredImportDialog",
  props: {
    cancel: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Object,
      default: () => ({}),
    },
    policyEndDate: {
      type: Date,
      default: null,
    },
    policyId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["custom-event", "custom-insert"],
  data() {
    return {
      tableData: [],
      descr: null,
      groupOptions: [],
      fileName: "",
    };
  },
  computed: {
    effectivePolicyId() {
      return (
        Number(this.policyId) ||
        Number(getPolicyId(this.$route)) ||
        (sessionStorage.getItem("policy") ? Number(JSON.parse(sessionStorage.getItem("policy"))) : 0) ||
        0
      );
    },
    invalidCount() {
      if (!this.tableData.length) return 0;
      return this.tableData.reduce((count, row) => {
        const missing = [
          row.pin,
          row.lastName,
          row.firstName,
          row.middleName,
          row.sex,
          row.dob,
          row.phone,
          row.address,
          row.groupId,
          row.startDate,
          row.isEmployee,
        ].filter((v) => v == null || v === "").length;
        const familyMissing =
          row.isEmployee === false &&
          (!row.insuredDependantId || !row.insuredDependantId.toString().trim())
            ? 1
            : 0;
        return count + missing + familyMissing;
      }, 0);
    },
  },
  mounted() {
    const pId = this.effectivePolicyId;
    if (pId) {
      const data = {
        id: pId,
        requestCorrelationId: "string",
      };
      Source.Groups(data).then((response) => {
        this.groupOptions = response.data.data;
      });
    }
  },
  methods: {
    handleFileChange(file) {
      this.fileName = file?.name || "";
      const reader = new FileReader();
      const headers = [
        "pin",
        "lastName",
        "firstName",
        "middleName",
        "sex",
        "dob",
        "phone",
        "address",
        "groupId",
        "isEmployee",
        "startDate",
        "vip",
        "insuredDependantId",
      ];
      reader.onload = (e) => {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        let data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        data = data.filter((row) => row.length > 0);
        data = data.slice(1);
        data.forEach((row) => {
          row[0] = row[0] ? row[0].toString().trim().toUpperCase() : null;
          row[1] = row[1] ? row[1].toString().trim() : null;
          row[2] = row[2] ? row[2].toString().trim() : null;
          row[3] = row[3] ? row[3].toString().trim() : null;
          row[4] = normalizeSex(row[4]);
          row[5] = parseExcelDate(row[5]);
          row[6] = row[6] ? row[6].toString().trim() : null;
          row[7] = row[7] ? row[7].toString().trim() : null;
          row[8] = matchGroupId(row[8], this.groupOptions);
          row[9] = normalizeEmployeeStatus(row[9]);
          row[10] = parseExcelDate(row[10]);
          row[11] = String(row[11] || "").trim().toUpperCase() === "VIP" || row[11] === true;
          row[12] = row[12] ? row[12].toString().trim().toUpperCase() : null;
        });

        this.tableData = data
          .filter((row) => row.length > 0)
          .map((row) =>
            Object.fromEntries(row.map((value, index) => [headers[index], value]))
          );
      };
      reader.readAsBinaryString(file.raw);
    },
    insert() {
      const hasInvalidFamilyMember = this.tableData.some(
        (row) =>
          row.isEmployee === false &&
          (!row.insuredDependantId || !row.insuredDependantId.toString().trim())
      );

      if (hasInvalidFamilyMember) {
        ElNotification({
          title: "Xəbərdarlıq",
          message: `${this.$t("mainInsured")}: ${this.$t("commonRequiredField")}`,
          type: "warning",
        });
        return;
      }

      this.tableData.forEach((row) => {
        row.dob = new Date(new Date(row.dob).setHours(new Date(row.dob).getHours() + 4));
        row.startDate = new Date(
          new Date(row.startDate).setHours(new Date(row.startDate).getHours() + 4)
        );
        if (row.insuredDependantId) {
          row.insuredDependantId = row.insuredDependantId.toString().trim().toUpperCase();
        }
        if (row.isEmployee !== false) {
          row.insuredDependantId = null;
        }
      });

      const items = this.tableData.map((row) => ({
        cardId: null,
        cardFullNumber: null,
        pin: row.pin,
        firstName: row.firstName,
        lastName: row.lastName,
        middleName: row.middleName,
        dob: row.dob,
        sex: row.sex,
        phone: row.phone,
        address: row.address,
        isEmployee: row.isEmployee,
        vip: row.vip,
        oldGroupId: null,
        newGroupId: row.groupId,
        startDate: row.startDate,
        endDate: null,
      }));

      const bulkPayload = {
        requestCorrelationId: "string",
        policyId: Number(this.effectivePolicyId),
        operType: 1,
        groupId: null,
        effectiveDate: null,
        descr: this.descr,
        createUser: 0,
        items: items,
      };

      Source.AccountRequestBulkInsert(bulkPayload)
        .then(() => {
          notifyRequestSuccess({
            count: this.tableData.length,
            policyId: this.effectivePolicyId,
            isBulk: true,
          });
          this.$emit("custom-insert", 1);
        })
        .catch(() => {
          const data = {
            requestCorrelationId: "string",
            commandUserId: 0,
            policyId: this.effectivePolicyId,
            descr: this.descr,
            import: this.tableData,
          };
          return Source.PolicyCardImportRequest(data).then(() => {
            notifyRequestSuccess({
              count: this.tableData.length,
              policyId: this.effectivePolicyId,
              isBulk: true,
            });
            this.$emit("custom-insert", 1);
          });
        });
    },
    downloadExample(name = "Nümunə.xlsx") {
      const basePath = process.env.BASE_URL || "/";
      const fileUrl = `${basePath}${name}`;
      return fetch(fileUrl)
        .then((res) => {
          if (!res.ok) throw new Error("File not found");
          return res.blob();
        })
        .then((blob) => {
          saveAs(blob, name);
          return true;
        })
        .catch((err) => {
          console.error("Download failed, using direct link fallback:", err);
          const link = document.createElement("a");
          link.href = fileUrl;
          link.setAttribute("download", name);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },
    disabledDate(time) {
      const dayInMillis = 24 * 60 * 60 * 1000;
      const policyEndDate = new Date(this.policyEndDate);
      return (
        time.getTime() <= new Date() - dayInMillis ||
        time.getTime() > policyEndDate.getTime()
      );
    },
    close() {
      this.$emit("custom-event", 0);
    },
  },
};
</script>

<style scoped>
.import-dialog {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.import-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--app-border, #e8eef5);
  border-radius: 12px;
  background: var(--app-surface-elevated, #fafbfd);
}

.import-toolbar__left,
.import-toolbar__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
}

.import-upload :deep(.el-upload) {
  display: inline-flex;
}

.import-action {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.25rem;
  padding: 0.45rem 0.95rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 650;
  cursor: pointer;
  transition: filter 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.import-action i {
  font-size: 0.85rem;
}

.import-action--primary {
  color: #fff;
  background: linear-gradient(135deg, var(--bs-info) 0%, var(--app-brand-deep) 100%);
  box-shadow: 0 2px 10px color-mix(in srgb, var(--bs-info) 28%, transparent);
}

.import-action--primary:hover {
  filter: brightness(1.04);
}

.import-action--outline {
  color: #10b981;
  background: var(--app-surface, #fff);
  border-color: rgba(16, 185, 129, 0.35);
}

.import-action--outline:hover {
  background: rgba(16, 185, 129, 0.1);
  border-color: #10b981;
}

.import-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 220px;
  padding: 0.28rem 0.6rem;
  border-radius: 999px;
  background: var(--app-surface-muted, #eef4fb);
  color: var(--app-heading, #1f2937);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid var(--app-border, transparent);
}

.import-chip--warn {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border-color: rgba(245, 158, 11, 0.3);
}

.import-chip--file {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.3);
}

.import-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 2rem 1.25rem;
  border: 1px dashed var(--app-border-strong, #d7dee8);
  border-radius: 12px;
  background: var(--app-surface, #fafbfd);
  text-align: center;
}

.import-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  margin-bottom: 0.75rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  font-size: 1.25rem;
}

.import-empty__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-heading, #1f2937);
}

.import-empty__text {
  margin: 0.4rem 0 0;
  max-width: 24rem;
  font-size: 0.86rem;
  color: var(--app-text-muted, #6b7280);
  line-height: 1.4;
}

.import-table-wrap {
  border: 1px solid var(--app-border, #e8eef5);
  border-radius: 12px;
  overflow: hidden;
  background: var(--app-surface, #fff);
}

.import-table :deep(.el-table__header th) {
  background: var(--app-surface-elevated, #f8fafc) !important;
  color: var(--app-heading, #475569);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.import-table :deep(.el-table__cell) {
  padding: 0.35rem 0.4rem;
}

.import-row-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.45rem;
  height: 1.45rem;
  border-radius: 6px;
  background: var(--app-surface-muted, #f1f5f9);
  color: var(--app-text-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 700;
}

.import-notes {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.import-notes__label {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--app-text-muted, #64748b);
}

.import-notes :deep(.el-textarea__inner) {
  background: var(--app-surface, #fff);
  color: var(--app-text, #1f2937);
  border-color: var(--app-border, #e8eef5);
}

.uppercase-input :deep(.el-input__inner) {
  text-transform: uppercase;
}

:deep(.is-invalid-field .el-input__wrapper),
:deep(.is-invalid-field.el-select .el-select__wrapper),
:deep(.is-invalid-field.el-date-editor .el-input__wrapper) {
  box-shadow: 0 0 0 1px #f87171 inset !important;
}

:deep(.import-input .el-input__inner),
:deep(.import-input .el-select__selected-item) {
  font-size: 0.78rem;
}

.insured-dialog-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: none;
  box-shadow: none;
}

/* Dark mode explicit overrides */
html[data-theme="dark"] .import-toolbar {
  background: #111c44;
  border-color: rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"] .import-empty {
  background: rgba(17, 28, 68, 0.6);
  border-color: rgba(255, 255, 255, 0.15);
}

html[data-theme="dark"] .import-empty__title {
  color: #ffffff;
}

html[data-theme="dark"] .import-empty__text {
  color: #a0aec0;
}

html[data-theme="dark"] .import-action--outline {
  background: rgba(16, 185, 129, 0.12);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.3);
}

html[data-theme="dark"] .import-action--outline:hover {
  background: rgba(16, 185, 129, 0.22);
}

html[data-theme="dark"] .import-table-wrap {
  background: #111c44;
  border-color: rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"] .import-table :deep(.el-table__header th) {
  background: #1a234a !important;
  color: #e2e8f0;
}

html[data-theme="dark"] .import-row-index {
  background: rgba(255, 255, 255, 0.06);
  color: #a0aec0;
}

html[data-theme="dark"] .import-notes :deep(.el-textarea__inner) {
  background: #111c44;
  color: #e2e8f0;
  border-color: rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .import-toolbar {
    align-items: stretch;
  }

  .import-toolbar__left,
  .import-toolbar__meta {
    width: 100%;
  }
}
</style>
